import { OrderStatus } from '@dazzle/api-client';

export const formatDate = (dateStr: string | undefined | null) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

export const formatDateShort = (dateStr: string | number | undefined | null) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

export const formatPrice = (price: number | undefined | null) => {
  if (!price) return 'N/A';
  return `$${price.toFixed(2)}`;
};

export const getStatusColor = (status: OrderStatus | undefined) => {
  if (!status) return '#8890A5';
  switch (status) {
    case OrderStatus.SuggestionPending:
      return '#F06724';
    case OrderStatus.PaymentFailed:
      return '#ef4444';
    case OrderStatus.OrderCompleted:
      return '#10b981';
    default:
      return '#008AFF';
  }
};

export const formatRelativeTime = (dateStr: string | number | undefined | null): string => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) return formatDateShort(dateStr);
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay === 1) return 'Yesterday';
    if (diffDay <= 7) return `${diffDay}d ago`;
    return formatDateShort(dateStr);
  } catch {
    return 'N/A';
  }
};

export const getStatusLabel = (status: OrderStatus | undefined) => {
  if (!status) return 'Unknown';
  switch (status) {
    case OrderStatus.SuggestionPending:
      return 'Suggestion Pending';
    case OrderStatus.ConfirmedByDazzle:
      return 'Dazzle Confirmed';
    case OrderStatus.DeclinedByDazzle:
      return 'Dazzle Declined';
    case OrderStatus.ConfirmedByUser:
      return 'User Confirmed';
    case OrderStatus.DeclinedByUser:
      return 'User Declined';
    case OrderStatus.OrderPending:
      return 'Order Placed';
    case OrderStatus.PaymentCompleted:
      return 'Payment Completed';
    case OrderStatus.PaymentFailed:
      return 'Payment Failed';
    case OrderStatus.OrderCompleted:
      return 'Order Delivered';
    case OrderStatus.OrderClosed:
      return 'Order Closed';
    case OrderStatus.ResearchPending:
      return 'Research Pending';
    default: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustiveCheck: never = status;
      return 'Unknown';
    }
  }
};

export const getValidNextStatuses = (status: OrderStatus): OrderStatus[] => {
  switch (status) {
    case OrderStatus.SuggestionPending:
      return [OrderStatus.ConfirmedByDazzle, OrderStatus.DeclinedByDazzle, OrderStatus.OrderClosed];
    case OrderStatus.ConfirmedByDazzle:
      return [OrderStatus.ConfirmedByUser, OrderStatus.DeclinedByUser, OrderStatus.OrderClosed];
    case OrderStatus.DeclinedByDazzle:
      return [OrderStatus.OrderClosed];
    case OrderStatus.ConfirmedByUser:
      return [OrderStatus.OrderPending, OrderStatus.OrderClosed];
    case OrderStatus.OrderPending:
      // Next status will be triggered by collecting payment, either will be PaymentCompleted or PaymentFailed
      return [OrderStatus.OrderClosed];
    case OrderStatus.DeclinedByUser:
      return [OrderStatus.OrderClosed];
    case OrderStatus.PaymentCompleted:
      return [OrderStatus.OrderCompleted, OrderStatus.OrderClosed];
    case OrderStatus.PaymentFailed:
      // Next status will be triggered by collecting payment, either will be PaymentCompleted or PaymentFailed
      return [OrderStatus.OrderClosed];
    case OrderStatus.OrderCompleted:
      return [OrderStatus.OrderClosed];
    case OrderStatus.OrderClosed:
      return [];
    case OrderStatus.ResearchPending:
      return [OrderStatus.ConfirmedByDazzle, OrderStatus.OrderClosed];
    default: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustive: never = status;
      return [];
    }
  }
};
